import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import PublicHome from "@/pages/PublicHome";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ServiceOrders from "@/pages/ServiceOrders";
import ServiceOrderDetail from "@/pages/ServiceOrderDetail";
import Users from "@/pages/Users";
import Roles from "@/pages/Roles";
import Settings from "@/pages/Settings";
import LojaVirtual from "@/pages/LojaVirtual";
import Pdv from "@/pages/Pdv";
import Inventory from "@/pages/Inventory";
import Devices from "@/pages/Devices";
import Clients from "@/pages/Clients";
import PosVenda from "@/pages/PosVenda";
import AlphaMap from "@/pages/AlphaMap";
import Reports from "@/pages/Reports";
import FinancialEvolution from "@/pages/FinancialEvolution";
import ReviewRedirect from "@/pages/ReviewRedirect";
import Seminovos from "@/pages/Seminovos";
import AcompanharOS from "@/pages/AcompanharOS";
import BoletoCelular from "@/pages/BoletoCelular";
import BoletoAdmin from "@/pages/BoletoAdmin";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Roadmap from "@/pages/Roadmap";
import Denied from "@/pages/Denied";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0C]">
        <div className="h-10 w-10 rounded-full border-2 border-gold border-t-transparent animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/equipe" replace />;
  return children;
}

function Guard({ perm, adminOnly, children }) {
  const { user, hasPerm } = useAuth();
  const ok = adminOnly ? user?.role === "admin" : (!perm || hasPerm(perm));
  return ok ? children : <Denied />;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
            <Route path="/" element={<PublicHome />} />
            <Route path="/seminovos" element={<Seminovos />} />
            <Route path="/acompanhar" element={<AcompanharOS />} />
            <Route path="/boleto" element={<BoletoCelular />} />
            <Route path="/privacidade" element={<PrivacyPolicy />} />
            <Route path="/avaliar" element={<ReviewRedirect />} />
            <Route path="/equipe" element={<Login />} />
            <Route path="/app" element={<Protected><Layout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="os" element={<Guard perm="service_orders.view"><ServiceOrders /></Guard>} />
              <Route path="os/:id" element={<Guard perm="service_orders.view"><ServiceOrderDetail /></Guard>} />
              <Route path="usuarios" element={<Guard perm="users.view"><Users /></Guard>} />
              <Route path="cargos" element={<Guard perm="roles.view"><Roles /></Guard>} />
              <Route path="configuracoes" element={<Guard perm="settings.view"><Settings /></Guard>} />
              <Route path="loja" element={<Guard perm="loja_virtual.view"><LojaVirtual /></Guard>} />
              <Route path="pdv" element={<Guard perm="pdv.view"><Pdv /></Guard>} />
              <Route path="estoque" element={<Guard perm="inventory.view"><Inventory /></Guard>} />
              <Route path="aparelhos" element={<Guard perm="device_purchase.view"><Devices /></Guard>} />
              <Route path="celulares-boleto" element={<Guard perm="device_sale.view"><BoletoAdmin /></Guard>} />
              <Route path="clientes" element={<Guard perm="clients.view"><Clients /></Guard>} />
              <Route path="pos-venda" element={<Guard perm="pos_sale.view"><PosVenda /></Guard>} />
              <Route path="mapa" element={<Guard perm="clients.view"><AlphaMap /></Guard>} />
              <Route path="relatorios" element={<Guard perm="reports.view"><Reports /></Guard>} />
              <Route path="evolucao-financeira" element={<Guard perm="reports.view"><FinancialEvolution /></Guard>} />
              <Route path="roadmap" element={<Guard adminOnly><Roadmap /></Guard>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster theme="dark" position="top-right" toastOptions={{
            style: { background: "#121217", border: "1px solid rgba(212,175,55,0.3)", color: "#fff" },
          }} />
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
